import {Button, Checkbox, CheckboxGroup, FormControl, IconButton, Textarea} from '@primer/react'
import {Dialog} from '@primer/react/deprecated'
import {Feedback, type FeedbackState} from '../routes/playground/components/GettingStartedDialog/types'
import {useCallback, useEffect, useMemo, useState, type FormEvent} from 'react'
import type {ShowModelPayload} from '../types'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ThumbsdownIcon, ThumbsupIcon} from '@primer/octicons-react'
import {sendFeedback} from '../utils/feedback'
import styles from './FeedbackDialog.module.css'

export interface FeedbackDialogProps {
  returnFocusRef: React.RefObject<HTMLDivElement | null>
  isFeedbackDialogOpen: boolean
  setIsFeedbackDialogOpen: (isFeedbackDialogOpen: boolean) => void
  isNegativePreSelected?: boolean
  setIsNegativeFeedbackConfirmed?: (isNegativeFeedbackConfirmed: boolean) => void
  modelName: string
}

const feedbackOptions = Object.values(Feedback)
function parseAsFeedback(value: string): Feedback {
  const numericValue = Number(value)
  if (new Set<number>(feedbackOptions).has(numericValue)) {
    return numericValue as Feedback
  }
  throw new Error(`Invalid Feedback of "${value}". Valid options are ${JSON.stringify(feedbackOptions)}`)
}

export function FeedbackDialog({
  returnFocusRef,
  isFeedbackDialogOpen,
  setIsFeedbackDialogOpen,
  isNegativePreSelected,
  setIsNegativeFeedbackConfirmed,
  modelName,
}: FeedbackDialogProps) {
  const payload = useRoutePayload<ShowModelPayload>()
  const {canProvideAdditionalFeedback, model} = payload
  const initialState = useMemo(
    () => ({
      satisfaction: Feedback.UNKNOWN,
      reasons: [],
      feedbackText: '',
      contactConsent: false,
      model: modelName,
    }),
    [modelName],
  )

  const [feedback, setFeedback] = useState<FeedbackState>(initialState)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)

  const isNegative = feedback.satisfaction === Feedback.NEGATIVE || isNegativePreSelected

  useEffect(() => {
    // If the user selects the thumbs down icon on the playground page, display the feedback dialog without the thumbs up/down options
    if (isNegativePreSelected) {
      setFeedback({...feedback, satisfaction: Feedback.NEGATIVE})
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)
    try {
      const res = await sendFeedback({model, feedback})
      if (res.ok) {
        setFeedback(initialState)
        setIsNegativeFeedbackConfirmed?.(true)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
    setIsFeedbackDialogOpen(false)
    setIsSubmitting(false)
  }, [feedback, initialState, model, setIsFeedbackDialogOpen, setIsNegativeFeedbackConfirmed])

  useEffect(() => {
    if (!canProvideAdditionalFeedback && feedback.satisfaction === Feedback.POSITIVE) {
      // The feedback gets instantly submitted when a user cannot provide additional feedback and clicks the thumbs up icon
      handleSubmit()
    }
    // handleSubmit does not need to be included in the dependency list because it gets triggered every time feedback changes
    // This useEffect should only be triggered when feedback.satisfaction or canProvideAdditionalFeedback changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canProvideAdditionalFeedback, feedback.satisfaction])

  return (
    <Dialog
      returnFocusRef={returnFocusRef}
      onDismiss={() => {
        setIsFeedbackDialogOpen(false)
      }}
      isOpen={isFeedbackDialogOpen}
      aria-labelledby="feedback-header"
      className={styles.feedbackDialog}
    >
      <Dialog.Header id="feedback-header" className={styles.feedbackDialogHeader}>
        Provide feedback
      </Dialog.Header>
      <form className="tmp-p-3 d-flex flex-column gap-3">
        {!isNegativePreSelected && <ThumbsUpDownButtons feedback={feedback} setFeedback={setFeedback} />}
        {isNegative && <NegativeFeedbackCheckboxGroup feedback={feedback} setFeedback={setFeedback} />}
        {canProvideAdditionalFeedback && <AdditionalInfoFields feedback={feedback} setFeedback={setFeedback} />}
        {(canProvideAdditionalFeedback || isNegative) && (
          <FeedbackFormControls feedback={feedback} handleSubmit={handleSubmit} isSubmitting={isSubmitting} />
        )}
      </form>
    </Dialog>
  )
}

function ThumbsUpDownButtons({
  feedback,
  setFeedback,
}: {
  feedback: FeedbackState
  setFeedback: (f: FeedbackState) => void
}) {
  const handleClick = useCallback(
    (event: FormEvent<HTMLButtonElement>) => {
      const response = parseAsFeedback(event.currentTarget.value)
      if (response === Feedback.POSITIVE) {
        setFeedback({...feedback, satisfaction: response, reasons: []})
      } else {
        setFeedback({...feedback, satisfaction: response})
      }
    },
    [feedback, setFeedback],
  )

  return (
    <div className="d-flex flex-column">
      <h4 className="f5">How has your experience been with GitHub Models?</h4>
      <div className="d-flex mt-2">
        <IconButton
          className={`color-fg-muted ${
            feedback.satisfaction === Feedback.POSITIVE ? 'color-canvas-subtle' : 'color-canvas-default'
          }`}
          icon={ThumbsupIcon}
          variant="invisible"
          aria-label="Positive"
          value="1"
          onClick={handleClick}
        />
        <IconButton
          className={`color-fg-muted ${
            feedback.satisfaction === Feedback.POSITIVE ? 'color-canvas-subtle' : 'color-canvas-default'
          }`}
          icon={ThumbsdownIcon}
          variant="invisible"
          aria-label="Negative"
          value="2"
          onClick={handleClick}
        />
      </div>
    </div>
  )
}

function FeedbackFormControls({
  feedback,
  handleSubmit,
  isSubmitting,
}: {
  feedback: FeedbackState
  handleSubmit: () => Promise<void>
  isSubmitting: boolean
}) {
  const submitButtonDisabled =
    (feedback.satisfaction === Feedback.UNKNOWN && feedback.feedbackText.length === 0) || isSubmitting

  return (
    <div className="d-flex mt-2 flex-justify-end">
      <Button variant="primary" onClick={handleSubmit} disabled={submitButtonDisabled}>
        Submit feedback
      </Button>
    </div>
  )
}

function AdditionalInfoFields({
  feedback,
  setFeedback,
}: {
  feedback: FeedbackState
  setFeedback: (f: FeedbackState) => void
}) {
  return (
    <>
      <FormControl>
        <FormControl.Label>Additional information</FormControl.Label>
        <Textarea
          aria-label="Additional information"
          resize="vertical"
          rows={3}
          value={feedback.feedbackText}
          placeholder="Give as much detail and context as possible but do not share any personal or sensitive information."
          onChange={e => {
            setFeedback({
              ...feedback,
              feedbackText: e.target.value,
            })
          }}
          className="width-full"
        />
      </FormControl>
      <FormControl>
        <Checkbox
          value="default"
          checked={feedback.contactConsent}
          onChange={() => {
            setFeedback({...feedback, contactConsent: !feedback.contactConsent})
          }}
        />
        <FormControl.Label className="text-normal">
          Allow GitHub to follow up about this feedback using the primary email address on your account.
        </FormControl.Label>
      </FormControl>
    </>
  )
}

function NegativeFeedbackCheckboxGroup({
  feedback,
  setFeedback,
}: {
  feedback: FeedbackState
  setFeedback: (f: FeedbackState) => void
}) {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const {value, checked} = event.target
      if (checked) {
        setFeedback({...feedback, reasons: [...feedback.reasons, value] as FeedbackState['reasons']})
      } else {
        setFeedback({
          ...feedback,
          reasons: feedback.reasons.filter(reason => reason !== value),
        })
      }
    },
    [feedback, setFeedback],
  )

  return (
    <CheckboxGroup>
      <CheckboxGroup.Label className={styles.checkboxGroupLabel}>This response is...</CheckboxGroup.Label>
      <FormControl>
        <Checkbox value="harmful" onChange={handleChange} />
        <FormControl.Label className="text-normal">Harmful or unsafe</FormControl.Label>
      </FormControl>
      <FormControl>
        <Checkbox value="not true" onChange={handleChange} />
        <FormControl.Label className="text-normal">Not true</FormControl.Label>
      </FormControl>
      <FormControl>
        <Checkbox value="unhelpful" onChange={handleChange} />
        <FormControl.Label className="text-normal">Not helpful</FormControl.Label>
      </FormControl>
      <FormControl>
        <Checkbox value="other" onChange={handleChange} />
        <FormControl.Label className="text-normal">Other</FormControl.Label>
      </FormControl>
    </CheckboxGroup>
  )
}
